import React from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import { DataDownloadFile, ReportStatus } from 'redux/schemas/api/custom-downloads';
import NvIcon from 'shared/components/nv-icon';
import { quarterSpacing, halfSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';
import { formatReportDate } from './data-downloads-card';

type DataDownloadsTableProps = {
  isGenerating: boolean;
  currentUserName: string;
  visibleFiles: DataDownloadFile[];
  isDailySftp?: boolean;
};

const styles = css`
  .table {
    & > tbody > tr > td {
      padding-top: 18px;
      padding-bottom: 18px;
    }

    & > thead > tr > th {
      border-bottom: none;

      &.generated-at {
        width: 220px;
      }

      &.download {
        width: 80px;
      }
    }
  }

  .new-badge {
    padding: 2px ${quarterSpacing}px;
    margin-right: ${halfSpacing}px;
    text-transform: uppercase;
    border-radius: 0;
  }
`;

const isNewFile = (file: DataDownloadFile, isDailySftp: boolean) => (
  !!isDailySftp && !!file.lastVisitedAt && !!file.finishedAt && file.lastVisitedAt < file.finishedAt
);

const getGeneratedAtCell = (file: DataDownloadFile, isDailySftp: boolean) => (
  <>
    {file.reportFileStatus === ReportStatus.FAILED && (
      <span className='label text-danger mr-1'>
        {t.CUSTOM_DOWNLOADS.FAILED()}
      </span>
    )}
    {isNewFile(file, isDailySftp) && (
      <span className='new-badge bg-success condensed label'>
        {t.CUSTOM_DOWNLOADS.NEW()}
      </span>
    )}
    {file.finishedAt && (
      <span>
        {formatReportDate(file.finishedAt, 'MOMENT.MONTH_DAY_COMMA_YEAR_AT_TIME')}
      </span>
    )}
    {file.reportFileStatus !== ReportStatus.FAILED && !file.finishedAt && (
      <span className='color-gray-2'>
        {t.CUSTOM_DOWNLOADS.PREPARING_DOWNLOAD()}
      </span>
    )}
  </>
);

const DataDownloadsTable = ({
  isGenerating,
  currentUserName,
  visibleFiles,
  isDailySftp = false,
}: DataDownloadsTableProps) => (
  <div css={styles} className='mx-5'>
    <table className='table w-100 mb-0 bottom-border border-gray-6'>
      <thead>
        <tr className='text-small text-gray-3'>
          <th className='generated-at font-weight-bold'>
            {t.CUSTOM_DOWNLOADS.GENERATED_AT()}
          </th>
          <th className='font-weight-bold'>
            {t.CUSTOM_DOWNLOADS.BY()}
          </th>
          <th className='download' aria-label={t.FILE_UPLOAD.DOWNLOAD()} />
        </tr>
      </thead>
      <tbody>
        {isGenerating && (
          <tr>
            <td className='generated-at pl-0'>
              <span className='color-gray-2'>
                {t.CUSTOM_DOWNLOADS.PREPARING_DOWNLOAD()}
              </span>
            </td>
            <td>{currentUserName || '-'}</td>
            <td className='download'>
              <NvIcon
                icon='refresh'
                size='smallest'
                className='spin text-primary text-center'
              />
            </td>
          </tr>
        )}
        {visibleFiles.map((file) => (
          <tr key={file.fileId}>
            <td className='generated-at pl-0'>
              {getGeneratedAtCell(file, isDailySftp)}
            </td>
            <td>{file.userName || '-'}</td>
            <td className='download'>
              {file.reportFileStatus === ReportStatus.GENERATED && file.url && (
                <a
                  href={file.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  download
                  className='text-primary link'
                  data-qa={config.pendo.customDownloads.listModal.downloadFile}
                >
                  {t.FILE_UPLOAD.DOWNLOAD()}
                </a>
              )}
            </td>
          </tr>
        ))}
        {!isGenerating && visibleFiles.length === 0 && (
          <tr>
            <td colSpan={3} className='text-center color-gray-2'>
              {t.CUSTOM_DOWNLOADS.NO_DOWNLOAD_GENERATED()}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export default DataDownloadsTable;
